import { useEffect, useState } from "react";

import { useRedline } from "@libs/redline";

import { identityEngageCustomerData } from "@libs/acp/apis/signIn/signInAPI";
import useAuthenticatedUser from "@libs/acp/hooks/useAuthenticatedUser";

import type { IdentitySignInUserEmailStatus } from "@libs/acp/apis/signIn/types";
import constants from "@libs/acp/common/constants";

type CardId = "with-debt" | "without-debt" | "notification" | "input";

const NEGATIVE_CACHE_TTL = 5 * 60 * 1000; // 5min

type UpdateEmailCustomEvent = {
	cardId: CardId;
	emailStatus: IdentitySignInUserEmailStatus;
};

export default function useEngageEmailStatus() {
	const { track: trackRedline } = useRedline();

	const [_, setEventsSent] = useState<string[]>([]);

	const { authenticatedUser, updateACPUSerContactStatus } =
		useAuthenticatedUser();
	const getEmailStatus = async () => {
		const requiredUpdates = authenticatedUser?.customer?.required_updates;
		if (requiredUpdates && requiredUpdates.length > 0) return;

		let isExpired = true;

		const emailStatusData = authenticatedUser?.customer?.contactStatus?.email;
		if (!emailStatusData) return;

		const emailStatusDataExpires = emailStatusData.expires;

		if (emailStatusDataExpires > 0) {
			isExpired = emailStatusDataExpires < Date.now();
		}

		if (isExpired) {
			const setNegativeCache = () => {
				updateACPUSerContactStatus("email", {
					...emailStatusData,
					expires: Date.now() + NEGATIVE_CACHE_TTL,
				});
			};

			try {
				const accessToken = authenticatedUser.auth.access_token;

				if (!accessToken) {
					throw new Error("Access token não encontrado");
				}

				const { data } = await identityEngageCustomerData();

				if (data) {
					const castData = data as unknown as {
						data: IdentitySignInUserEmailStatus;
					};

					const mappedData = {
						...castData.data,
						expires: Date.now() + 60 * 60 * 1000, // +1 hour
					} as IdentitySignInUserEmailStatus;

					updateACPUSerContactStatus("email", mappedData);
				} else {
					setNegativeCache();
				}
			} catch (error) {
				console.error("Failed to fetch email status:", error);
				setNegativeCache();
			}
		}
	};

	const handleElementViewed = ({
		detail: { cardId, emailStatus },
	}: CustomEvent<UpdateEmailCustomEvent>) => {
		if (!cardId) return;

		const { bounced, customer_engagement_status: disabled } = emailStatus || {};

		let eventStatus = "";
		const disabledStatus = disabled ? `disabled-${disabled}` : "";

		if (bounced && disabled) eventStatus = `bounced-${disabledStatus}`;
		else if (bounced) eventStatus = "bounced";
		else if (disabled) eventStatus = disabledStatus;

		const eventName = `updated-email-${eventStatus}-${cardId}`;

		const defaultPayload = {
			location: "home",
			elementType: "card",
			text: "Atualize o e-mail para manter sua conta segura",
			eventName,
		};

		const payload = {
			"with-debt": defaultPayload,
			"without-debt": defaultPayload,
			notification: {
				location: "notification-bar",
				elementType: "alert",
				text: "Atualize o e-mail para manter sua conta segura",
				eventName,
			},
			input: {
				location: "my-account",
				elementType: "input",
				text: "Atualize o e-mail para manter sua conta segura",
				eventName,
			},
			unsub: {
				location: "home",
				elementType: "card",
				text: "Estamos sentindo sua falta!",
				eventName: "subscribe-email-unsub",
			},
		};

		setEventsSent((prev) => {
			const { eventName, ...eventPayload } = payload[cardId];

			if (prev.includes(eventName)) return prev;

			trackRedline.userTracking.elementViewed({
				name: eventName,
				...eventPayload,
			});

			return [...prev, eventName];
		});
	};

	useEffect(() => {
		document.addEventListener(
			constants.customDispatchEvents.EmailBouncedCTAViewed,
			(event) =>
				handleElementViewed(event as CustomEvent<UpdateEmailCustomEvent>),
		);

		return () => {
			document.removeEventListener(
				constants.customDispatchEvents.EmailBouncedCTAViewed,
				(event) =>
					handleElementViewed(event as CustomEvent<UpdateEmailCustomEvent>),
			);
		};
	}, []);

	return { getEmailStatus };
}
